import { darken, adaptV4Theme } from '@mui/material/styles';
import { createTheme, lighten } from '@mui/material';
import { opacity } from './utils';

const common = {
  black: '#2c2c2e', // Even lighter black
  background: '#48484a',
  white: '#ffffff',
  red: '#ff0000',
  increase: '#BEF2D7',
  decrease: '#F9CDBA',
  border: 'rgba(255,255,255,0.28)', // Adjusted for dark mode
  grey: {
    50: '#3a3a3c', // Lighter grey
    100: '#48484a',
    200: '#636366',
    300: '#8e8e93',
    400: '#aeaeb2',
    500: '#c7c7cc',
    600: '#d1d1d6',
    700: '#e5e5ea',
    800: '#f2f2f7',
    900: '#ffffff',
  },
  primaryColor: '#90caf9',
  secondaryColor: '#f48fb1',
};

const palette = (
  primaryColor = '#90caf9',
  secondaryColor = '#f48fb1',
  partnerTheme = null,
  tertiaryColor = null,
  borderWidth = null,
  borderRadius = null,
  borderColor = null,
  boxShadow = null,
) => {
  let background_color,
    border_color,
    border_radius,
    border_width,
    box_shadow,
    font_family,
    primary_color,
    secondary_color,
    tertiary_color,
    text_color;

  if (partnerTheme) {
    // console.log('here is partnerTheme in light theme: ', partnerTheme)
    primaryColor = partnerTheme.primary_color != null ? partnerTheme.primary_color : primaryColor;
    secondaryColor = partnerTheme.secondary_color != null ? partnerTheme.secondary_color : secondaryColor;
    background_color = partnerTheme.background_color != null ? partnerTheme.background_color : '';
    border_color = partnerTheme.border_color != null ? partnerTheme.border_color : '';
    border_radius = partnerTheme.border_radius != null ? partnerTheme.border_radius : '';
    border_width = partnerTheme.border_width != null ? partnerTheme.border_width : '';
    box_shadow = partnerTheme.box_shadow != null ? partnerTheme.box_shadow : '';
    font_family = partnerTheme.font_family != null ? partnerTheme.font_family : '';
  }

  if (tertiaryColor) {
    tertiary_color = tertiaryColor;
  }
  if (borderWidth) {
    border_width = borderWidth;
  }
  if (borderRadius) {
    border_radius = borderRadius;
  }
  if (borderColor) {
    border_color = borderColor;
  }
  if (boxShadow) {
    box_shadow = boxShadow;
  }

  return {
    common: common,
    primary: {
      main: primaryColor,
      dark: darken(primaryColor, 0.1), // Less dark
      light: lighten(primaryColor, 0.3), // More light
      contrastText: '#000000',
      background: darken(primaryColor, 0.75), // Lighter background
      backgroundHover: darken(primaryColor, 0.4),
      border: primaryColor,
      ...(partnerTheme?.primary_color && { primary_color: partnerTheme.primary_color }),
      ...(partnerTheme?.background_color && { background_color: partnerTheme.background_color }),
      ...(partnerTheme?.border_color && { border_color: partnerTheme.border_color }),
      ...(partnerTheme?.border_radius && { border_radius: partnerTheme.border_radius }),
      ...(partnerTheme?.border_width && { border_width: partnerTheme.border_width }),
      ...(partnerTheme?.box_shadow && { box_shadow: partnerTheme.box_shadow }),
      ...(partnerTheme?.font_family && { font_family: partnerTheme.font_family }),
    },

    border: {
      color: border_color || 'white',
      width: border_width || '1px',
      radius: border_radius || '4px',
    },

    secondary: {
      main: secondaryColor,
      dark: darken(secondaryColor, 0.1), // Less dark
      light: lighten(secondaryColor, 0.3), // More light
      contrastText: '#000000',
      background: darken(secondaryColor, 0.75), // Lighter background
      backgroundHover: darken(secondaryColor, 0.4),
      border: secondaryColor,
      ...(partnerTheme?.secondary_color && { secondary_color: partnerTheme.secondary_color }),
    },
    admin: {
      main: '#ffffff',
      dark: darken('#ffffff', 0.05), // Less dark
      light: lighten('#ffffff', 0.3), // More light
      bodyText: lighten('#ffffff', 0.5),
      contrastText: darken('#ffffff', 0.3),
      background: darken('#ffffff', 0.85), // Lighter background
      backgroundHover: darken('#ffffff', 0.4),
      border: darken('#ffffff', 0.7),
      tab: darken('#ffffff', 0.4),
      activeTab: darken('#ffffff', 0.7),
      trendChart: darken('#ffffff', 0.7),
      starter: darken('#ffffff', 0.7),
      premium: darken('#ffffff', 0.6),
      enterprise: darken('#ffffff', 0.5),
      recommended: '#e2a787',
    },
    error: {
      main: '#f44336',
      dark: darken('#f44336', 0.1), // Less dark
      light: lighten('#f44336', 0.3), // More light
      contrastText: '#000000',
      background: darken('#f44336', 0.75), // Lighter background
      backgroundHover: darken('#f44336', 0.4),
      border: '#f44336',
    },
    warning: {
      main: '#ffb74d',
      dark: darken('#ffb74d', 0.1), // Less dark
      light: lighten('#ffb74d', 0.3), // More light
      contrastText: '#000000',
      background: darken('#ffb74d', 0.75), // Lighter background
      backgroundHover: darken('#ffb74d', 0.4),
      border: '#ffb74d',
    },
    success: {
      main: '#66bb6a',
      dark: darken('#66bb6a', 0.1), // Less dark
      light: lighten('#66bb6a', 0.3), // More light
      contrastText: '#000000',
      background: darken('#66bb6a', 0.75), // Lighter background
      backgroundHover: darken('#66bb6a', 0.4),
      border: '#66bb6a',
    },
    info: {
      main: '#29b6f6',
      dark: darken('#29b6f6', 0.1), // Less dark
      light: lighten('#29b6f6', 0.3), // More light
      contrastText: '#000000',
      background: darken('#29b6f6', 0.75), // Lighter background
      backgroundHover: darken('#29b6f6', 0.4),
      border: '#29b6f6',
    },
    background: {
      paper: '#2e2e2e', // Even lighter background
      default: '#3e3e3e', // Even lighter default
    },
  };
};

const theme = (
  primaryColor,
  secondaryColor,
  partnerTheme = null,
  tertiaryColor = null,
  borderWidth = null,
  borderRadius = null,
  borderColor = null,
  boxShadow = null,
) => {
  const finalPalette = palette(
    primaryColor,
    secondaryColor,
    partnerTheme,
    tertiaryColor,
    borderWidth,
    borderRadius,
    borderColor,
    boxShadow,
  );

  return createTheme(
    adaptV4Theme({
      themeName: 'Dark',
      palette: finalPalette,
      globalClasses: {
        screen: {
          width: '96%',
          margin: '10px auto auto',
        },
        adminTableHead: {
          borderRadius: 5,
          color: finalPalette.admin.main,
          backgroundColor: finalPalette.admin.activeTab,
          '& *': {
            color: finalPalette.admin.main,
            fontWeight: 'bold',
          },
          '& .MuiTableSortLabel-active': {
            color: finalPalette.admin.dark,
          },
        },
        adminTableRow: {
          borderRadius: 5,
          color: finalPalette.admin.bodyText,
          backgroundColor: finalPalette.admin.trendChart,
          '& *': {
            color: finalPalette.admin.bodyText,
          },
          '& .MuiTableSortLabel-active': {
            color: finalPalette.admin.dark,
          },
        },
        adminTableRowWhite: {
          borderRadius: 5,
          color: finalPalette.admin.bodyText,
          backgroundColor: finalPalette.common.black,
          '& *': {
            color: finalPalette.admin.bodyText,
          },
          '& .MuiTableSortLabel-active': {
            color: finalPalette.admin.dark,
          },
        },
        patientChartCard: {
          cursor: 'pointer',
          color: finalPalette.common.grey['300'],
          padding: createTheme().spacing(1),
          width: '100%',
          height: 'auto',
          '& h1': {
            color: finalPalette.admin.main,
            fontWeight: 'bold',
            fontSize: 22,
          },
          '& h2': {
            color: finalPalette.admin.main,
            fontWeight: 'bold',
            fontSize: 14,
          },
          '& h3': {
            color: finalPalette.admin.main,
            fontWeight: 'bold',
            fontSize: 12,
          },
          '& a': {
            color: finalPalette.admin.main,
            fontWeight: 'bold',
          },
          '& textarea': {
            borderRadius: 5,
            padding: 5,
          },
        },
      },
      typography: {
        useNextVariants: true,
        htmlFontSize: 16,
        fontFamily: '"SharpBook19", "Helvetica", "Arial", sans-serif',
        fontSize: 14,
        fontWeightLight: 400,
        fontWeightRegular: 400,
        fontWeightMedium: 400,
        fontWeightBold: 400,
        h1: {},
        h2: {
          fontWeight: 600,
        },
        h3: {},
        h4: {},
        h5: {},
        h6: {},
        body1: {
          color: finalPalette.admin.bodyText,
          '& b': {
            color: finalPalette.admin.main,
            fontWeight: 'bold',
          },
        },
        body2: {
          color: finalPalette.admin.bodyText,
        },
        subtitle1: {
          fontWeight: 600,
          width: '100%',
          marginBottom: 100,
          fontSize: 24,
          color: finalPalette.admin.main,
        },
        subtitle2: {},
        helper: {
          primary: finalPalette.common.grey['100'],
          secondary: opacity(finalPalette.common.grey['100'], 0.54),
          light: '#DFE9E9',
        },
      },
      overrides: {
        MuiButton: {
          root: {},
          contained: {},
          outlined: {},
          text: {},
          sizeSmall: {},
          sizeLarge: {},
          styleOverrides: {
            root: {
              textTransform: 'none',
            },
          },
        },
      },
      breakpoints: {
        values: {
          xs: 320,
          sm: 641,
          md: 960,
          lg: 1080,
          xl: 1920,
        },
      },
    }),
  );
};

export default theme;
